/**
 * Product Floating Bar
 * Handles visibility, cart actions, and urgency message rotation
 */

(function() {
  'use strict';

  // Store initialized instances
  const instances = new Map();

  /**
   * Initialize a floating bar instance
   * @param {string} blockId - The block ID
   * @param {Object} options - Configuration options
   */
  window.initProductFloatingBar = function(blockId, options = {}) {
    const bar = document.querySelector(`#product-floating-bar-${blockId}`);
    if (!bar || instances.has(blockId)) return;

    const instance = new ProductFloatingBar(bar, options);
    instances.set(blockId, instance);
    return instance;
  };

  class ProductFloatingBar {
    constructor(element, options = {}) {
      this.element = element;
      this.options = {
        urgencyInterval: options.urgencyInterval || 3000,
        visibilityMode: options.visibilityMode || 'always'
      };

      this.variantId = element.dataset.variantId;
      this.productAvailable = element.dataset.productAvailable === 'true';
      
      this.urgencyBanner = element.querySelector('[data-urgency-banner]');
      this.urgencyItems = element.querySelectorAll('[data-urgency-item]');
      this.buttons = element.querySelectorAll('[data-floating-btn]');
      
      this.urgencyIndex = 0;
      this.urgencyTimer = null;
      this.observer = null;
      this.lastScrollY = 0;
      this.scrollHandler = null;
      this.isDrawerOpen = false;

      this.init();
    }

    init() {
      this.setupVisibility();
      this.setupButtonListeners();
      this.setupVariantChangeListener();
      this.setupDrawerListeners();
      
      if (this.urgencyItems.length > 1) {
        this.startUrgencyRotation();
      }

      // Initialize Lucide icons if available
      if (typeof lucide !== 'undefined') {
        lucide.createIcons({
          icons: {},
          attrs: { 'stroke-width': 2 }
        });
      }
    }

    /**
     * Setup listeners for drawers to hide floating bar
     */
    setupDrawerListeners() {
      // List of events that indicate a drawer is opening
      const openEvents = [
        'cart:opened',
        'wishlist:opened',
        'search:opened',
        'menu:opened',
        'offers:opened',
        'modal:opened'
      ];

      // List of events that indicate a drawer is closing
      const closeEvents = [
        'cart:closed',
        'wishlist:closed',
        'search:closed',
        'menu:closed',
        'offers:closed',
        'modal:closed'
      ];

      // Hide on open
      openEvents.forEach(event => {
        window.addEventListener(event, () => {
          this.isDrawerOpen = true;
          this.hide();
        });
      });

      // Restore visibility check on close
      closeEvents.forEach(event => {
        window.addEventListener(event, () => {
          this.isDrawerOpen = false;
          // Trigger a scroll check or visibility check
          if (this.scrollHandler) {
            this.scrollHandler();
          } else {
            this.setupVisibility();
          }
        });
      });
    }

    /**
     * Setup visibility based on selected mode
     */
    setupVisibility() {
      // Don't show if a drawer is open
      if (this.isDrawerOpen || document.body.classList.contains('cart-drawer-open') || document.body.classList.contains('wishlist-drawer-open') || document.body.classList.contains('menu-drawer-open') || document.body.classList.contains('search-drawer-open')) {
        this.hide();
        return;
      }

      if (this.options.visibilityMode === 'scroll_up') {
        this.setupScrollUpVisibility();
      } else {
        // Default: always visible
        this.show();
      }
    }

    /**
     * Show when scrolling up, hide when scrolling down
     */
    setupScrollUpVisibility() {
      this.lastScrollY = window.scrollY;
      
      // Remove existing handler if any
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      
      this.scrollHandler = () => {
        if (this.isDrawerOpen) return;
        
        const currentScrollY = window.scrollY;
        
        if (currentScrollY < this.lastScrollY || currentScrollY < 100) {
          this.show();
        } else {
          this.hide();
        }
        
        this.lastScrollY = currentScrollY;
      };
      
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      
      // Show initially
      this.show();
    }

    /**
     * Show the floating bar
     */
    show() {
      // Final safety check for open drawers
      if (this.isDrawerOpen || document.body.classList.contains('cart-drawer-open') || document.body.classList.contains('wishlist-drawer-open')) {
        this.hide();
        return;
      }
      this.element.classList.add('is-visible');
    }

    /**
     * Hide the floating bar
     */
    hide() {
      this.element.classList.remove('is-visible');
    }

    /**
     * Setup click listeners for buttons
     */
    setupButtonListeners() {
      this.buttons.forEach((button) => {
        const btnType = button.dataset.floatingBtn;

        button.addEventListener('click', (e) => {
          // Custom links are handled by browser
          if (btnType === 'custom') return;
          
          e.preventDefault();

          switch (btnType) {
            case 'add_to_cart':
              this.handleAddToCart(button);
              break;
            case 'buy_now':
              this.handleBuyNow(button);
              break;
          }
        });
      });
    }

    /**
     * Listen for variant changes and update button states
     */
    setupVariantChangeListener() {
      // product.js dispatches variant:changed on window — listen only there.
      const onVariantChanged = (e) => this.handleVariantChange(e.detail);
      window.addEventListener('variant:changed', onVariantChanged);

      // Also listen for Shopify's native variant change
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="id"], select[name="id"]')) {
          const variantId = e.target.value;
          this.updateVariantId(variantId);
        }
      });
    }

    /**
     * Handle variant change
     * @param {Object} detail - Variant change detail
     */
    handleVariantChange(detail) {
      if (detail && detail.variant) {
        this.updateVariantId(detail.variant.id);
        this.updateAvailability(detail.variant.available);
      }
    }

    /**
     * Update the variant ID on all buttons
     * @param {string} variantId - New variant ID
     */
    updateVariantId(variantId) {
      this.variantId = variantId;
      this.element.dataset.variantId = variantId;
      
      this.buttons.forEach((button) => {
        if (button.dataset.variantId !== undefined) {
          button.dataset.variantId = variantId;
        }
      });
      
      // Update wishlist buttons too
      const wishlistBtns = this.element.querySelectorAll('[data-wishlist-btn]');
      wishlistBtns.forEach(btn => {
        btn.dataset.variantId = variantId;
        // Also update price data attributes if variant data is available
        // Note: We might not have full variant object here to update price
      });
    }

    /**
     * Update button availability states
     * @param {boolean} available - Whether variant is available
     */
    updateAvailability(available) {
      this.productAvailable = available;
      
      this.buttons.forEach((button) => {
        const btnType = button.dataset.floatingBtn;
        
        if (btnType === 'add_to_cart' || btnType === 'buy_now') {
          button.disabled = !available;
          const textEl = button.querySelector('[data-btn-text]');
          if (textEl) {
            if (!available) {
              textEl.textContent = 'Sold Out';
            } else {
              // Restore original text based on button type
              textEl.textContent = btnType === 'add_to_cart' ? 'Add to Cart' : 'Buy Now';
            }
          }
        }
      });
    }

    /**
     * Handle Add to Cart button click
     * @param {HTMLElement} button - The clicked button
     */
    async handleAddToCart(button) {
      if (button.disabled) return;

      const variantId = button.dataset.variantId || this.variantId;
      if (!variantId) return;

      this.setButtonLoading(button, true);

      try {
        // Use Cart API if available
        if (window.CartAPI) {
          await window.CartAPI.addItem(parseInt(variantId, 10), 1);
        } else {
          // Fallback to fetch
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: parseInt(variantId, 10),
                quantity: 1
              }]
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }
          
          const data = await response.json();
          // Dispatch legacy event for fallback
          window.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: data } // Note: CartAPI usually fetches full cart, here we just have added item response, but event listener usually fetches cart
          }));
        }

        this.setButtonSuccess(button);

        // Cart drawer listens on window — must dispatch there, not document.
        window.dispatchEvent(new CustomEvent('cart:item-added'));

      } catch (error) {
        console.error('Add to cart error:', error);
        this.setButtonError(button);
      }
    }

    /**
     * Handle Buy Now button click
     * @param {HTMLElement} button - The clicked button
     */
    handleBuyNow(button) {
      if (button.disabled) return;

      const variantId = button.dataset.variantId || this.variantId;
      if (!variantId) return;

      // Navigate directly — Shopify adds to cart and redirects server-side,
      // so the browser shows the progress bar immediately instead of waiting
      // for an AJAX roundtrip first.
      window.location.href = '/cart/add?id=' + variantId + '&quantity=1&return_to=%2Fcheckout';
    }

    /**
     * Set button loading state
     * @param {HTMLElement} button - The button element
     * @param {boolean} loading - Whether loading
     */
    setButtonLoading(button, loading) {
      if (loading) {
        button.classList.add('is-loading');
        button.disabled = true;
      } else {
        button.classList.remove('is-loading');
        button.disabled = !this.productAvailable;
      }
    }

    /**
     * Set button success state temporarily
     * @param {HTMLElement} button - The button element
     */
    setButtonSuccess(button) {
      button.classList.remove('is-loading');
      button.classList.add('is-success');
      
      const textEl = button.querySelector('[data-btn-text]');
      const originalText = textEl ? textEl.textContent : '';
      
      if (textEl) {
        textEl.textContent = 'Added!';
      }

      setTimeout(() => {
        button.classList.remove('is-success');
        button.disabled = !this.productAvailable;
        if (textEl) {
          textEl.textContent = originalText;
        }
      }, 2000);
    }

    setButtonError(button) {
      button.classList.remove('is-loading');
      const textEl = button.querySelector('[data-btn-text]');
      const originalText = textEl ? textEl.textContent : '';
      if (textEl) textEl.textContent = 'Error — Try Again';
      setTimeout(() => {
        button.disabled = !this.productAvailable;
        if (textEl) textEl.textContent = originalText;
      }, 2000);
    }

    /**
     * Start urgency message rotation
     */
    startUrgencyRotation() {
      this.urgencyTimer = setInterval(() => {
        this.rotateUrgencyMessage();
      }, this.options.urgencyInterval);
    }

    /**
     * Rotate to next urgency message
     */
    rotateUrgencyMessage() {
      if (this.urgencyItems.length <= 1) return;

      // Remove active from current
      this.urgencyItems[this.urgencyIndex].classList.remove('is-active');

      // Move to next
      this.urgencyIndex = (this.urgencyIndex + 1) % this.urgencyItems.length;

      // Add active to new
      this.urgencyItems[this.urgencyIndex].classList.add('is-active');
    }

    /**
     * Destroy the instance and cleanup
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
      
      if (this.urgencyTimer) {
        clearInterval(this.urgencyTimer);
      }
      
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  // Auto-initialize on DOMContentLoaded for bars without explicit init
  document.addEventListener('DOMContentLoaded', function() {
    const bars = document.querySelectorAll('[data-product-floating-bar]');
    bars.forEach((bar) => {
      const blockId = bar.id.replace('product-floating-bar-', '');
      if (!instances.has(blockId)) {
        window.initProductFloatingBar(blockId);
      }
    });
  });
})();
